<template>
  <form
    ref="searchForm"
    :action="$variable('routes.search')"
    class="search-bar"
    :class="{
      'search-bar--focused': searchActive && isMobile
    }"
    @blur.capture="handleFormBlur"
  >
    <ais-instant-search
      :index-name="algoliaIndicies.product"
      :search-client="searchClient"
    >
      <ais-index :index-name="algoliaIndicies.suggestion" />
      <ais-index :index-name="algoliaIndicies.collection" />
      <ais-index :index-name="algoliaIndicies.page" />

      <ais-autocomplete>
        <template #default="{ currentRefinement, indices }">
          <ais-search-box
            :class-names="{
              'ais-SearchBox': 'search-bar__header',
            }"
          >
            <template #default="{ refine }">
              <div class="search-bar__box">
                <input
                  id="search"
                  ref="input"
                  class="search-bar__input text-body-s-desktop"
                  type="search"
                  name="query"
                  autocomplete="off"
                  spellcheck="false"
                  :placeholder="
                    isMobile ?
                      $string('search.placeholder_mobile') :
                      $string('search.placeholder_desktop')
                  "
                  :value="localQuery"
                  @focus="handleInputFocus"
                  @input="handleInput(refine, $event.currentTarget.value)"
                  @keydown.esc="clearInput(refine)"
                >

                <button
                  v-show="currentRefinement === '' || !isMobile"
                  type="submit"
                  class="search-bar__submit button button--icon"
                  @click="searchBarDataLayer(currentRefinement)"
                >
                  <span
                    class="visually-hidden"
                    v-text="$string('search.title')"
                  />
                  <icon-search />
                </button>

                <button
                  v-show="currentRefinement !== '' && isMobile"
                  ref="clearInputButton"
                  type="button"
                  class="search-bar__clear button button--icon"
                  @click="clearInput(refine)"
                >
                  <span
                    class="visually-hidden"
                    v-text="$string('search.clear')"
                  />
                  <icon-close />
                </button>

                <button
                  v-if="searchActive"
                  type="button"
                  class="search-bar__close search-bar__close--desktop"
                  @click="handleClose(refine)"
                >
                  <span
                    class="text-body-xs"
                    v-text="$string('search.clear')"
                  />
                </button>
              </div>

              <button
                v-if="searchActive"
                type="button"
                class="search-bar__close search-bar__close--mobile"
                @click="handleClose(refine)"
              >
                <span
                  class="text-body-xs"
                  v-text="$string('search.cancel')"
                />
              </button>
            </template>
          </ais-search-box>

          <search-overlay
            ref="searchOverlay"
            :current-refinement="currentRefinement"
            :indices="indices"
            :search-active="searchActive"
            :popular-search-link-list="popularSearchLinkList"
            :overlay-start="searchOverlayStart"
            @results-updated="handleResultsUpdate"
          />
        </template>
      </ais-autocomplete>
    </ais-instant-search>
  </form>
</template>

<script>

/**
 * Vue: Search bar (search-bar)
 * -----------------------------------------------------------------------------
 * Search bar used in the header.
 * - Static section.
 *
 */
import { mapActions, mapState } from 'vuex'
import { nextTick } from '@vue/runtime-core'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

import algoliasearch from 'algoliasearch/lite'
import { AisSearchBox, AisAutocomplete, AisInstantSearch, AisIndex } from 'vue-instantsearch/vue3/es'

import timings from '~/config/timings'
import { algoliaIndicies } from '~/config/algolia'

import SearchOverlay from '~global/search-overlay/search-overlay'
import IconClose from '~icons/misc/close.svg'
import IconSearch from '~icons/misc/search.svg'

const algoliaClient = algoliasearch(
  cnvs.algolia.applicationId,
  cnvs.algolia.searchApiKey,
)

const searchClient = {
  ...algoliaClient,
  search(requests) {
    if (requests.every(({ params }) => !params.query)) {
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
        })),
      })
    }

    return algoliaClient.search(requests)
  },
}

export default {
  name: 'SearchBar',

  components: {
    AisAutocomplete,
    AisInstantSearch,
    AisIndex,
    AisSearchBox,
    IconClose,
    IconSearch,
    SearchOverlay,
  },

  props: {
    popularSearchLinkList: {
      type: Object,
      default: () => ({
        items: [],
      }),
    },
  },

  emits: ['searchFocus', 'searchBlur'],

  data() {
    return {
      focused: false,
      searchClient,
      algoliaIndicies,
      searchActive: false,
      searchOverlayStart: 0,
      searchOverlayState: 'idle',
      localQuery: new URLSearchParams(document.location.search.substring(1)).get('query'),
      inputDebounce: null,
      nodeSelectors: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      previousElement: (state) => state.accessibility.previousElement,
    }),
  },

  watch: {
    searchActive() {
      if (this.searchActive) {
        this.getScrollbarGap()
        nextTick(() => {
          disableBodyScroll(this.$refs.searchOverlay.$el, { reserveScrollBarGap: true })
          this.addSiteHeaderOffset()
          this.updateFocusState()
        })

        return
      }

      enableBodyScroll(this.$refs.searchOverlay.$el)
      this.removeSiteHeaderOffset()
      this.updateFocusState()
    },
  },

  mounted() {
    this.setNodeSelectors()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      setFocusTrap: 'accessibility/setFocusTrap',
      setPreviousElement: 'accessibility/setPreviousElement',
    }),

    /**
     * Set node selectors.
     */
    setNodeSelectors() {
      this.nodeSelectors = {
        siteHeaderTop: document.querySelector('[js-site-header="siteHeaderTop"]'),
        siteHeaderPromoDesktop: document.querySelector('[js-site-header="siteHeaderPromoDesktop"]'),
      }
    },

    clearInput(refine) {
      refine('')
      this.localQuery = ''
    },

    handleInputFocus() {
      this.$emit('searchFocus')
      this.setSearchOverlayStart()

      nextTick(() => {
        this.searchActive = true
      })
    },

    handleFormBlur(event) {

      /**
       * If the new focus element is in the form, do nothing.
       */
      if (this.$refs.searchForm?.contains(event.relatedTarget)) {
        return
      }

      this.$refs.clearInputButton.click()
      this.$emit('searchBlur')
      this.searchActive = false
    },

    /**
     * Handle input event on the seach input.
     * - Sets the searchActive state to true if it isn't already.
     * - Updates the `localQuery` value
     * - Calls the `refine` function from Algolia to search Algolia with the new
     *   query.
     */
    handleInput(refine, value) {
      if (!this.searchActive) {
        this.searchActive = true
      }

      this.localQuery = value

      if (refine instanceof Function) {

        clearTimeout(this.inputDebounce)
        this.inputDebounce = setTimeout(() => {
          refine(value)
        }, timings.normal)
      }
    },

    handleResultsUpdate(updateData) {
      if (this.searchActive) {
        this.updateFocusState()
      }

      this.handleOverlayStateChange(updateData)
    },

    handleOverlayStateChange(updateData) {
      if (updateData.hasResults && this.$refs.input.value) {
        this.searchOverlayState = 'results'
        return
      }

      if (this.$refs.input.value) {
        this.searchOverlayState = 'no-results'
        return
      }

      return this.searchOverlayState = 'idle'
    },

    handleClose(refine) {
      this.searchActive = false
      this.$emit('searchBlur')
      this.clearInput(refine)
    },

    /**
     * Update focus trap using Vuex.
     */
    updateFocusState() {
      if (this.$refs.input.value) {
        this.setFocusTrap({ container: this.$refs.searchForm })

        /**
         * Have to use timeout to wait for animation to complete.
         * - Safari doesn't support `preventScroll`.
         */
        setTimeout(() => {
          if (!this.getFirstElement) {
            return
          }

          this.getFirstElement.focus({ preventScroll: true })
        }, timings.normal)

        return
      }

      this.clearFocusState()
    },

    /**
     * Get the width of the scrollbar.
     */
    getScrollbarGap() {
      this.scrollBarGap = window.innerWidth - document.documentElement.clientWidth
    },

    /**
     * Add right padding to the site header elements to stop scrollbar jump.
     */
    addSiteHeaderOffset() {
      if (
        !this.nodeSelectors.siteHeaderTop ||
        !this.scrollBarGap ||
        this.scrollBarGap === 0
      ) {
        return
      }

      this.nodeSelectors.siteHeaderTop.style.paddingRight = `${this.scrollBarGap}px`

      if (!this.nodeSelectors.siteHeaderPromoDesktop) {
        return
      }

      this.nodeSelectors.siteHeaderPromoDesktop.style.paddingRight = `${this.scrollBarGap}px`
    },

    /**
     * Remove right padding from the site header elements to stop scrollbar jump.
     */
    removeSiteHeaderOffset() {
      if (
        !this.nodeSelectors.siteHeaderTop ||
        !this.scrollBarGap ||
        this.scrollBarGap === 0
      ) {
        return
      }

      this.nodeSelectors.siteHeaderTop.style.paddingRight = `0px`

      if (!this.nodeSelectors.siteHeaderPromoDesktop) {
        return
      }

      this.nodeSelectors.siteHeaderPromoDesktop.style.paddingRight = `0px`
    },

    /**
     * Clear focus and previous element.
     */
    clearFocusState() {
      this.clearFocusTrap()

      if (!this.previousElement || typeof this.previousElement !== 'object') {
        return
      }

      this.previousElement.focus()
      this.setPreviousElement()
    },

    setSearchOverlayStart() {
      const searchFormStart = this.$refs.searchForm.offsetLeft

      this.searchOverlayStart = this.isMobile ? 0 : searchFormStart
    },

    /**
     * Send the click event info to the data layer.
     * @param {String} label - search term.
     */
    searchBarDataLayer(label) {
      cnvs.EventBus.emit('Datalayer:searchBar', label.toLowerCase())
    },

    test() {
      const params = new URLSearchParams(document.location.search.substring(1))
      return params.get('query')
    },
  },
}
</script>

<style lang="scss">
@import './search-bar';
</style>
